import {
  createFileRoute,
  Link,
  Outlet,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router'
import { LogOut, Moon, Sun } from 'lucide-react'
import { useAuth } from '@/auth/AuthProvider'
import { RequireAuth } from '@/auth/RequireAuth'
import { Button } from '@/components/ui/controls'
import { useTheme } from '@/lib/theme'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/_app')({ component: AppLayout })

const NAV = [
  { to: '/matches', label: 'Matches' },
  { to: '/players', label: 'Players' },
] as const

/**
 * Every route under /_app sits behind RequireAuth, so the shell below only ever
 * renders for a signed-in subscriber. See auth/RequireAuth.tsx for the two
 * redirect paths and why they are kept apart.
 */
function AppLayout() {
  return (
    <RequireAuth>
      <AppShell />
    </RequireAuth>
  )
}

function AppShell() {
  const { signOut, email } = useAuth()
  const navigate = useNavigate()
  const pathname = useRouterState({ select: (s) => s.location.pathname })

  return (
    <div className="flex h-dvh flex-col overflow-hidden bg-app">
      <header className="z-30 shrink-0 border-b bg-app">
        <div className="flex h-14 items-center gap-6 px-4">
          <Link to="/matches" className="text-sm font-semibold tracking-tight text-fg">
            TTDB
          </Link>
          <nav className="flex items-center gap-1" aria-label="Main">
            {NAV.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={cn(
                  'rounded-md px-3 py-1.5 text-sm transition-colors',
                  pathname.startsWith(item.to)
                    ? 'bg-[var(--bg-hover)] text-fg'
                    : 'text-muted hover:text-fg',
                )}
              >
                {item.label}
              </Link>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-1">
            <ThemeToggle />
            <span className="hidden px-2 text-xs text-subtle sm:inline">{email}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => signOut().then(() => navigate({ to: '/login' }))}
              aria-label="Sign out"
            >
              <LogOut className="size-4" aria-hidden />
            </Button>
          </div>
        </div>
      </header>
      <main className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <Outlet />
      </main>
    </div>
  )
}

function ThemeToggle() {
  const { theme, toggle } = useTheme()
  return (
    <Button variant="ghost" size="sm" onClick={toggle} aria-label="Toggle theme">
      {theme === 'dark' ? (
        <Sun className="size-4" aria-hidden />
      ) : (
        <Moon className="size-4" aria-hidden />
      )}
    </Button>
  )
}
